import { ArrowDown, ArrowUp } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from 'zustand';

import { getTrendingSymbols } from '@/features/markets/utils/categories';
import useFundedAccount from '@/hooks/use-funded-account';
import { cn } from '@/lib/utils';
import { getMarketStore } from '@/store/use-markets-store';
import { useWebSocketStore } from '@/store/use-websocket-store';
import { abbrFromSym, imgFromSym } from '@/utils/token-symbol';
import { formatBigInt } from '@/utils/value-format';

export default function TrendingBanner() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLUListElement>(null);
  const [needsScrolling, setNeedsScrolling] = useState(false);

  const { isFundedOrEval } = useFundedAccount();

  const symbols = useMemo(
    () => getTrendingSymbols({ restricted: isFundedOrEval }),
    [isFundedOrEval],
  );

  useEffect(() => {
    const { increaseDependenciesForSymbol, decreaseDependenciesForSymbol } =
      useWebSocketStore.getState();
    symbols.forEach(increaseDependenciesForSymbol);
    return () => {
      symbols.forEach(decreaseDependenciesForSymbol);
    };
  }, [symbols]);

  // Check if scrolling is needed
  useEffect(() => {
    const checkWidth = () => {
      if (scrollRef.current && contentRef.current) {
        const containerWidth = scrollRef.current.offsetWidth;
        const contentWidth = contentRef.current.offsetWidth;
        setNeedsScrolling(contentWidth > containerWidth);
      }
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, [symbols]);

  if (symbols.size === 0) return;

  return (
    <div className="relative z-40 flex h-12 shrink-0 items-center gap-6 border-b border-border bg-background px-6 max-lg:hidden sm:px-7">
      <div
        ref={scrollRef}
        className="no-scrollbar flex-1 overflow-x-hidden overflow-y-hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <ul
          ref={contentRef}
          className={cn('inline-flex gap-6 whitespace-nowrap', {
            'justify-center': !needsScrolling,
            'animate-scroll-banner': needsScrolling,
          })}
        >
          {/* First set of symbols */}
          {Array.from(symbols, (symbol) => (
            <SymbolRef symbol={symbol} key={`first-${symbol}`} />
          ))}
          {/* Duplicate set for seamless scrolling (only when needed) */}
          {needsScrolling &&
            Array.from(symbols, (symbol) => (
              <SymbolRef symbol={symbol} key={`second-${symbol}`} />
            ))}
        </ul>
      </div>
    </div>
  );
}

type Props = {
  symbol: string;
};

const SymbolRef = ({ symbol }: Props) => {
  const priceChangePct = useStore(
    getMarketStore(symbol),
    (state) => state.marketData.priceChangePct,
  );
  return (
    <Link
      to={`/trade/${symbol}`}
      className="flex min-w-max items-center text-lg"
    >
      <img
        className="mr-3 inline-block size-4 rounded-full object-contain"
        src={imgFromSym(symbol)}
        alt=""
      />
      <span className="mr-2 inline-block font-semibold leading-none">
        {abbrFromSym(symbol)}
      </span>
      <span
        className={cn('flex items-center gap-1 font-semibold tabular-nums', {
          'text-green': priceChangePct > 0n,
          'text-red': priceChangePct < 0n,
          'text-vestgrey-100': priceChangePct === 0n,
        })}
      >
        {priceChangePct < 0n ? (
          <ArrowDown size={14} />
        ) : (
          priceChangePct > 0n && <ArrowUp size={14} />
        )}
        {formatBigInt(priceChangePct, 4n, {
          showChange: true,
          style: 'percent',
          digits: 2,
        })}
      </span>
    </Link>
  );
};

SymbolRef.displayName = 'SymbolRef';
